import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Calculator, Handshake, DollarSign, Calendar,
  TrendingUp, User, Users
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const SellerFinancing = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    property_name: "",
    sale_price: 400000,
    down_payment: 80000,
    interest_rate: 8,
    loan_term_years: 15,
    balloon_payment_year: null,
    seller_original_basis: 250000,
    seller_depreciation: 50000,
    compare_to_conventional: true,
    conventional_rate: 7
  });

  const [hasBalloon, setHasBalloon] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const calculate = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    setCalculating(true);
    try {
      const payload = {
        ...formData,
        balloon_payment_year: hasBalloon ? formData.balloon_payment_year : null
      };
      const response = await axios.post(`${API}/calculator/seller-financing`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data.results);
      toast.success("Seller financing calculated!");
    } catch (error) {
      toast.error("Failed to calculate");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="seller-financing-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Seller Financing Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              onClick={calculate}
              disabled={calculating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="calculate-seller-financing-btn"
            >
              {calculating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Deal Terms */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Handshake className="h-5 w-5 text-amber-500" />
                Deal Terms
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name *</Label>
                  <Input
                    value={formData.property_name}
                    onChange={(e) => handleInputChange('property_name', e.target.value)}
                    placeholder="Seller Financed Property"
                    className="bg-slate-950 border-slate-800 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Sale Price</Label>
                    <Input
                      type="number"
                      value={formData.sale_price}
                      onChange={(e) => handleInputChange('sale_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Down Payment</Label>
                    <Input
                      type="number"
                      value={formData.down_payment}
                      onChange={(e) => handleInputChange('down_payment', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={formData.interest_rate}
                      onChange={(e) => handleInputChange('interest_rate', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Loan Term (Years)</Label>
                    <Input
                      type="number"
                      value={formData.loan_term_years}
                      onChange={(e) => handleInputChange('loan_term_years', parseInt(e.target.value) || 15)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Balloon Payment */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-cyan-500" />
                  Balloon Payment
                </h3>
                <Switch
                  checked={hasBalloon}
                  onCheckedChange={setHasBalloon}
                />
              </div>
              {hasBalloon && (
                <div>
                  <Label className="text-slate-300">Balloon Due in Year</Label>
                  <Input
                    type="number"
                    value={formData.balloon_payment_year || 5}
                    onChange={(e) => handleInputChange('balloon_payment_year', parseInt(e.target.value) || 5)}
                    min={1}
                    max={formData.loan_term_years}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Remaining balance due at end of year {formData.balloon_payment_year || 5}
                  </p>
                </div>
              )}
            </div>

            {/* Seller Info */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-500" />
                Seller's Tax Basis (Optional)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Original Basis</Label>
                  <Input
                    type="number"
                    value={formData.seller_original_basis}
                    onChange={(e) => handleInputChange('seller_original_basis', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Depreciation Taken</Label>
                  <Input
                    type="number"
                    value={formData.seller_depreciation}
                    onChange={(e) => handleInputChange('seller_depreciation', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Comparison */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-purple-500" />
                  Compare to Conventional
                </h3>
                <Switch
                  checked={formData.compare_to_conventional}
                  onCheckedChange={(v) => handleInputChange('compare_to_conventional', v)}
                />
              </div>
              {formData.compare_to_conventional && (
                <div>
                  <Label className="text-slate-300">Conventional Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.125"
                    value={formData.conventional_rate}
                    onChange={(e) => handleInputChange('conventional_rate', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!result ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <Handshake className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Seller Financing Analysis</h3>
                <p className="text-slate-400 mb-6">Calculate payments and benefits for owner-financed deals</p>
                <div className="grid grid-cols-2 gap-4 text-sm text-left max-w-md">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <Users className="h-4 w-4 text-emerald-500 mb-1" />
                    <div className="text-slate-300 font-medium">For Buyers</div>
                    <div className="text-slate-500 text-xs">Flexible terms, easier qualification</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <User className="h-4 w-4 text-amber-500 mb-1" />
                    <div className="text-slate-300 font-medium">For Sellers</div>
                    <div className="text-slate-500 text-xs">Steady income, tax benefits</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Monthly Payment */}
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-300 mb-2">Monthly Payment</div>
                  <div className="text-4xl font-bold font-mono text-amber-500" data-testid="monthly-payment">
                    {formatCurrency(result.loan_details.monthly_payment)}
                  </div>
                  {result.buyer_analysis.monthly_savings > 0 && (
                    <div className="mt-2 text-sm text-emerald-500">
                      Save {formatCurrency(result.buyer_analysis.monthly_savings)}/mo vs conventional
                    </div>
                  )}
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Loan Amount</div>
                    <div className="text-xl font-bold font-mono text-white">
                      {formatCurrency(result.loan_details.loan_amount)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Down Payment</div>
                    <div className="text-xl font-bold font-mono text-cyan-500">
                      {formatCurrency(result.loan_details.down_payment)} ({result.loan_details.down_payment_percent}%)
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Interest</div>
                    <div className="text-xl font-bold font-mono text-red-400">
                      {formatCurrency(result.payment_summary.total_interest)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Payments</div>
                    <div className="text-xl font-bold font-mono text-white">
                      {formatCurrency(result.payment_summary.total_payments)}
                    </div>
                  </div>
                </div>

                {/* Balloon Payment */}
                {result.balloon_payment.has_balloon && (
                  <div className="glass-card rounded-xl p-6 border-l-4 border-cyan-500">
                    <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-cyan-500" />
                      Balloon Payment
                    </h3>
                    <div className="text-2xl font-bold font-mono text-cyan-500">
                      {formatCurrency(result.balloon_payment.balloon_amount)}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      Due at end of year {result.balloon_payment.balloon_year}
                    </div>
                  </div>
                )}

                {/* Balance Chart */}
                {result.yearly_summary && result.yearly_summary.length > 0 && (
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Loan Balance Over Time</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={result.yearly_summary}>
                          <defs>
                            <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="year" stroke="#64748b" />
                          <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="end_balance" 
                            stroke="#06b6d4" 
                            fill="url(#balanceGrad)"
                            name="Balance"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Seller Analysis */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-emerald-500" />
                    Seller's Perspective
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Total Income</span>
                      <span className="font-mono text-emerald-500">{formatCurrency(result.seller_analysis.total_income)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Interest Income</span>
                      <span className="font-mono text-white">{formatCurrency(result.seller_analysis.interest_income)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Yearly Interest Income</span>
                      <span className="font-mono text-white">{formatCurrency(result.seller_analysis.yearly_interest_income)}/yr</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-slate-400">Gross Profit Ratio</span>
                      <span className="font-mono text-amber-500">{result.seller_analysis.gross_profit_ratio}%</span>
                    </div>
                  </div>
                </div>

                {/* Buyer Comparison */}
                {result.buyer_analysis.conventional_payment && (
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-500" />
                      Buyer Comparison
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Seller Finance Payment</span>
                        <span className="font-mono text-white">{formatCurrency(result.buyer_analysis.monthly_payment)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Conventional Payment</span>
                        <span className="font-mono text-white">{formatCurrency(result.buyer_analysis.conventional_payment)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold">
                        <span className="text-slate-300">Total Savings</span>
                        <span className={`font-mono ${result.buyer_analysis.total_savings > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                          {formatCurrency(result.buyer_analysis.total_savings)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SellerFinancing;
